// src/store/playback
import { createSlice } from '@reduxjs/toolkit'
import { parseLivePath } from '~/lib/params'
import { addPin, pinKeysOf, removePin } from '~/lib/pinLayer'

// The session's playback, shared by every client over the data channel, with each key's type; the preset indices stay each client's own.
const SHARED_PLAYBACK = {
	playing: 'boolean',
	instant: 'boolean',
	loop: 'boolean',
	hold_duration: 'number',
	fade_duration: 'number',
	// The runner's open project (C-project): a remote lists it, nobody else reads it.
	project_id: 'string',
	// `project_id` names a template (fetched with `getTemplate`, never saved over).
	project_template: 'boolean',
}

const ofType = (v, type) => typeof v === type && (typeof v !== 'number' || Number.isFinite(v))

/** The keys of `playback` the session shares, any of the wrong type dropped. */
export const sharedPlayback = playback => {
	const out = {}
	for (const key in SHARED_PLAYBACK) {
		const v = playback?.[key]
		if (ofType(v, SHARED_PLAYBACK[key])) out[key] = v
	}
	return out
}

// Initial state for playback
export const initialState = {
	current_preset_index: -1, // Unified index: 0..userPresets.length-1 = user presets, userPresets.length+ = template presets
	next_preset_index: -1,
	instant: false,
	playing: false,
	loop: true,
	hold_duration: 5,
	fade_duration: 0,
	project_id: null, // The session's project as the runner published it (C-project); not this client's own `app.project_id`.
	project_template: false,
	cached_templates: null, // Cached template presets for instant display (bypasses RTK Query)
	cached_desktop_templates: null, // Cached desktop example presets for instant display (bypasses RTK Query)
	// The open project's pin layer (`~/lib/pinLayer`). A desktop hydrates it from the project; a remote mirrors the runner's.
	pins: {},
	// The project `pins` belongs to: the only one it is ever written to.
	pins_project: null,
	// Whether `pins` has met `live` since hydrating: until the first load restores its values, live holds the previous project's.
	pins_loaded: false,
	// Whether the running fade is frozen. Written by transitionManager from the GSAP timeline React cannot subscribe to; its position is `transitionManager.getProgress()`, never stored.
	transition_paused: false,
	// Whether this fade starts from a mid-fade SNAPSHOT rather than from a preset. True once a transition has been retargeted: `current_preset_index` still names the original origin, but the visual source is an interior point no preset represents, so nothing may be drawn against it.
	transition_detached: false,
	// Whether the bar slides LEFT. A property of the GESTURE, not of the index pair: only a backwards drag off the current preset reverses the travel.
	transition_reversed: false,
	// Every morph running right now, keyed by QUALIFIED param (`diffusion.prompt`), each an `{a, b, t}` envelope in the contract's own vocabulary. Absent = that param is at rest.
	//
	// Keyed rather than a single `prompt_morph` because `morph: true` is a contract flag any param may carry, so a singular slot makes the second one a refactor instead of a contract line. Not persisted, and not synced through this slice — the envelope reaches peers on the live patch.
	morphs: {},
}

// Create the playback slice
const playbackSlice = createSlice({
	name: 'playback',
	initialState,
	reducers: {
		setInstantTransitions: (s, { payload }) => {
			s.instant = payload
		},
		setCurrentPresetIndex: (s, { payload }) => {
			s.current_preset_index = payload
		},
		// Resets the fade's flags on the SAME action that changes the target, so start, retarget and finish need no caller to remember.
		setNextPresetIndex: (s, { payload }) => {
			s.next_preset_index = payload
			s.transition_paused = false
			s.transition_detached = false
			s.transition_reversed = false
		},
		setTransitionDetached: (s, { payload }) => {
			s.transition_detached = payload
		},
		setTransitionReversed: (s, { payload }) => {
			s.transition_reversed = payload
		},
		setTransitionPaused: (s, { payload }) => {
			s.transition_paused = payload
		},
		setPlaying: (s, { payload }) => {
			s.playing = payload
		},
		setHoldDuration: (s, { payload }) => {
			s.hold_duration = payload
		},
		setFadeDuration: (s, { payload }) => {
			s.fade_duration = payload
		},
		setLoop: (s, { payload }) => {
			s.loop = payload
		},
		setPlaybackState: (s, { payload }) => {
			Object.assign(s, payload)
		},
		setCachedTemplates: (s, { payload }) => {
			s.cached_templates = payload
		},
		setCachedDesktopTemplates: (s, { payload }) => {
			s.cached_desktop_templates = payload
		},
		/** `payload` is one live-prop key or a list. */
		addLiveProp: (s, { payload }) => {
			for (const key of [payload].flat()) addPin(s.pins, key)
		},
		removeLiveProp: (s, { payload }) => {
			for (const key of [payload].flat()) removePin(s.pins, key)
		},
		toggleLiveProp: (s, { payload }) => {
			if (pinKeysOf(s.pins).includes(payload)) removePin(s.pins, payload)
			else addPin(s.pins, payload)
		},
		/** Pins whose object stopped existing. `payload` is live-prop keys; each drops with every prop pinned beneath its object. */
		dropObjectPins: (s, { payload }) => {
			for (const key of payload) {
				const p = parseLivePath(key)
				if (p?.scope === 'object') removePin(s.pins, `objects.${p.id}`)
			}
		},
		/** A project's layer, as opened (desktop) or as the runner holds it (remote). */
		hydratePins: (s, { payload: { project_id, pins } }) => {
			s.pins = pins
			s.pins_project = project_id
			s.pins_loaded = false
		},
		/** Undo/redo: the open project's layer as a history entry held it. */
		restorePins: (s, { payload }) => {
			s.pins = payload
		},
		/** `[[layerPath, value], ...]` read from live; never creates a pin. */
		setPinValues: (s, { payload }) => {
			for (const [path, value] of payload) {
				let node = s.pins
				for (let i = 0; i < path.length - 1 && node; i++) node = node[path[i]]
				if (node && typeof node === 'object') node[path[path.length - 1]] = value
			}
		},
		/** Start (or update) one param's morph. `payload: [qualifiedKey, {a, b, t}]`. */
		setMorph: (s, { payload: [key, envelope] }) => {
			s.morphs[key] = envelope
		},
		/** End one param's morph, or ALL of them when called with no key. */
		clearMorph: (s, { payload }) => {
			if (payload === undefined) s.morphs = {}
			else delete s.morphs[payload]
		},
		/** A cue landed — `{ index, instant, sync, advance }` for `playbackSync` to broadcast; `advance` marks the run's own step. No state: every refusal in `loadPreset` must return before this, or peers get a cue nobody made. */
		presetLoaded: () => {},
	},
	extraReducers: b => {
		// A load restores the layer's values into live (`meta.pins`), so from here live is what the layer follows.
		b.addCase('live/setLiveObject', s => {
			s.pins_loaded = true
		})
	},
})

export default playbackSlice.reducer

export const {
	setInstantTransitions,
	setCurrentPresetIndex,
	setNextPresetIndex,
	setTransitionPaused,
	setTransitionDetached,
	setTransitionReversed,
	setHoldDuration,
	setFadeDuration,
	setLoop,
	setPlaying,
	setPlaybackState,
	setCachedTemplates,
	setCachedDesktopTemplates,
	addLiveProp,
	removeLiveProp,
	toggleLiveProp,
	dropObjectPins,
	hydratePins,
	restorePins,
	setPinValues,
	setMorph,
	clearMorph,
	presetLoaded,
} = playbackSlice.actions
